import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { trpc } from 'src/trpc';
import {
  getStoredOrganizationId,
  setStoredOrganizationId,
  clearStoredOrganizationId,
  isValidObjectId,
} from 'src/utils/organization-storage';

interface Organization {
  _id: string;
  name: string;
  status?: string;
}

interface OrganizationContextType {
  /** The currently selected organization ID */
  organizationId: string | null;
  /** True once the organization has been resolved from storage and validated */
  isResolved: boolean;
  /** True while transitioning between organizations (for loading states) */
  isOrganizationSwitching: boolean;
  /** Set the organization switching state */
  setIsOrganizationSwitching: (switching: boolean) => void;
  /** The list of organizations the user has access to */
  availableOrganizations: Organization[];
  /** The currently selected organization object */
  currentOrganization: Organization | null;
  /** Request an organization switch (may show confirmation dialog) */
  requestOrganizationSwitch: (newOrgId: string, onConfirm: () => void) => void;
  /** Directly set the organization ID (updates localStorage and invalidates queries) */
  setOrganizationId: (orgId: string) => void;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

// Entity detail page configurations for showing confirmation dialogs
const ENTITY_CONFIGS = [
  { pathPattern: '/orders/', dashboardPath: '/orders', entityDisplayName: 'Order Dashboard' },
  { pathPattern: '/drivers/', dashboardPath: '/drivers', entityDisplayName: 'Driver Dashboard' },
  { pathPattern: '/trucks/', dashboardPath: '/trucks', entityDisplayName: 'Truck Dashboard' },
  { pathPattern: '/invoices/', dashboardPath: '/invoices', entityDisplayName: 'Invoice Dashboard' },
];

interface PendingSwitch {
  newOrgId: string;
  onConfirm: () => void;
  entityDisplayName: string;
  dashboardPath: string;
}

interface OrganizationProviderProps {
  children: ReactNode;
}

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const [organizationId, setOrganizationIdState] = useState<string | null>(null);
  const [isResolved, setIsResolved] = useState(false);
  const [isOrganizationSwitching, setIsOrganizationSwitching] = useState(false);
  const [pendingSwitch, setPendingSwitch] = useState<PendingSwitch | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const utils = trpc.useUtils();

  // Fetch user's organizations
  const { data: currentUser, isLoading: isUserLoading } = trpc.admin.user.adminGetSelf.useQuery(
    undefined,
    {
      // Always fetch user data, even without org context
      // The backend will use fallback org if none is set
      staleTime: 30000,
      refetchOnWindowFocus: false,
    },
  );

  const availableOrganizations = currentUser?.organizations || [];

  // Resolve organization from localStorage on mount
  useEffect(() => {
    if (isUserLoading || !currentUser) {
      return;
    }

    const storedOrgId = getStoredOrganizationId();
    const userOrgs = currentUser.organizations || [];

    if (userOrgs.length === 0) {
      // User has no organizations
      setIsResolved(true);
      return;
    }

    // Validate stored org against user's available orgs
    if (storedOrgId && isValidObjectId(storedOrgId)) {
      const isValidOrg = userOrgs.some((org) => org._id === storedOrgId);
      if (isValidOrg) {
        setOrganizationIdState(storedOrgId);
        setIsResolved(true);
        return;
      }
    }

    // Clear invalid stored ID before falling back
    clearStoredOrganizationId();

    // Fallback to first organization
    const firstOrg = userOrgs[0];
    if (firstOrg?._id) {
      setOrganizationIdState(firstOrg._id);
      setStoredOrganizationId(firstOrg._id);
    }
    setIsResolved(true);
  }, [currentUser, isUserLoading]);

  // Find if we're on an entity details page (path ending with MongoDB ObjectId)
  const activeEntityConfig = ENTITY_CONFIGS.find((config) => {
    // Match paths like /orders/60d21b4667d0d8992e610c85 but not /orders/ or /orders
    const detailPageRegex = new RegExp(`^${config.pathPattern}[0-9a-fA-F]{24}$`);
    return detailPageRegex.test(location.pathname);
  });

  // Set organization ID (updates storage and invalidates queries)
  const setOrganizationId = useCallback(
    (orgId: string) => {
      setStoredOrganizationId(orgId);
      setOrganizationIdState(orgId);
      // Invalidate all queries to refetch with new organization context
      utils.invalidate();
    },
    [utils],
  );

  // Request organization switch (may show confirmation dialog)
  const requestOrganizationSwitch = useCallback(
    (newOrgId: string, onConfirm: () => void) => {
      if (activeEntityConfig) {
        // Show confirmation dialog for entity details pages
        setPendingSwitch({
          newOrgId,
          onConfirm: () => {
            setIsOrganizationSwitching(true);
            onConfirm();
            // Redirect to dashboard after switching
            navigate(activeEntityConfig.dashboardPath);
          },
          entityDisplayName: activeEntityConfig.entityDisplayName,
          dashboardPath: activeEntityConfig.dashboardPath,
        });
      } else {
        // Proceed with switch immediately for non-detail pages
        setIsOrganizationSwitching(true);
        onConfirm();
      }
    },
    [activeEntityConfig, navigate],
  );

  const handleConfirm = useCallback(() => {
    if (pendingSwitch) {
      pendingSwitch.onConfirm();
      setPendingSwitch(null);
    }
  }, [pendingSwitch]);

  const handleCancel = useCallback(() => {
    setPendingSwitch(null);
  }, []);

  const currentOrganization =
    availableOrganizations.find((org) => org._id === organizationId) || null;

  const value: OrganizationContextType = {
    organizationId,
    isResolved,
    isOrganizationSwitching,
    setIsOrganizationSwitching,
    availableOrganizations,
    currentOrganization,
    requestOrganizationSwitch,
    setOrganizationId,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
      <AlertDialog open={!!pendingSwitch} onOpenChange={(open) => !open && handleCancel()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="title">Switch Organization?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted">
              Switching organizations will redirect you to the{' '}
              <span className="font-bold">{pendingSwitch?.entityDisplayName || 'Dashboard'}</span>{' '}
              as this data will no longer be valid.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel} className="text-text-light">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}>Switch & Go</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </OrganizationContext.Provider>
  );
};

export const useOrganization = (): OrganizationContextType => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
};
